import { clsx } from 'clsx';
import { View } from 'react-native';
import { Product } from 'shared/@generated/models';
import { RatingStars } from 'shared/components/shop/rating-stars';
import { Text } from 'shared/components/text';

type RatedProduct = Product & {
  rating_score?: number | null;
  rating_votes?: number | null;
};

const clampRating = (rating: number) => Number(Math.max(1, Math.min(5, rating)).toFixed(1));

const formatRating = (rating: number) => rating.toFixed(1);

const getAggregateRating = (product: Product) => {
  const ratedProduct = product as RatedProduct;
  const ratingValue = ratedProduct.rating_score;
  const reviewCount = ratedProduct.rating_votes;

  if (
    typeof ratingValue !== 'number' ||
    !Number.isFinite(ratingValue) ||
    typeof reviewCount !== 'number' ||
    !Number.isFinite(reviewCount) ||
    reviewCount <= 0
  ) {
    return undefined;
  }

  return {
    ratingValue: clampRating(ratingValue),
    reviewCount: Math.max(1, Math.round(reviewCount)),
  };
};

export function ShopProductRating({
  product,
  className,
  size = 'md',
}: {
  product: Product;
  className?: string;
  size?: 'sm' | 'md';
}) {
  const aggregateRating = getAggregateRating(product);

  if (!aggregateRating) {
    return null;
  }

  return (
    <View
      className={clsx(
        'flex-row items-center self-start',
        {
          'gap-1.5': size === 'sm',
          'gap-2': size === 'md',
        },
        className,
      )}
    >
      <RatingStars value={aggregateRating.ratingValue} size={size} />

      <View className={'flex-row items-baseline'}>
        <Text
          className={clsx('text-brand-brown-dark font-semibold tracking-tight', {
            'text-xs': size === 'sm',
            'text-sm md:text-[15px]': size === 'md',
          })}
        >
          {formatRating(aggregateRating.ratingValue)}
        </Text>

        {size === 'md' && (
          <Text
            className={clsx('text-brand-brown-dark/55 ml-1 tracking-tight', {
              'text-xs md:text-sm': size === 'md',
            })}
          >
            ({aggregateRating.reviewCount.toLocaleString('sv-SE')})
          </Text>
        )}
      </View>
    </View>
  );
}
